import { useState, type MouseEvent } from 'react'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { IconButton, Popover, Stack, Typography } from '@mui/material'
import useTranslation from '@/hooks/useTranslation'

/**
 * SCRUM-1145 — why a decoder's purpose is marked red, behind an info button
 * next to it.
 *
 * This used to be a banner above the grid naming every flagged decoder, with
 * the whole row tinted red. QC found that too alarming for what is a warning,
 * so only the purpose is marked now, and the explanation is one click away on
 * the decoder it is about.
 */

export interface DecoderConflict {
  /**
   * `stranded`: the purpose is no longer a station purpose at all.
   * `mismatch`: still a station purpose, but the station is the other Type.
   */
  kind: 'stranded' | 'mismatch'
  /** The purpose the decoder has now. */
  purpose: string
  stations: string[]
  /**
   * A `mismatch` on an Irrigation decoder: what its Non Irrigation station is
   * missing before it can be made Irrigation to match, as field names from
   * `missingStationFields`. Empty when it has everything.
   */
  irrigationNeeds: string[]
}

/** The station grid's own column names, so the user looks for the same word. */
const FIELD_LABEL_KEYS: Record<string, string> = {
  plant: 'stationsColumnPlant',
  soil: 'stationsColumnSoil',
  emitter: 'stationsColumnEmitter',
  pipe: 'stationsColumnPipe',
  expectedFlow: 'stationsColumnExpectedFlow',
  precipitationRate: 'stationsColumnPrecipitationRate',
  priority: 'stationsColumnPriority',
}

const normalise = (value: string) => value.toLowerCase().replace(/[\s_-]+/g, '')

/** How to fix it, which depends on which way the two disagree. */
const hintFor = (
  conflict: DecoderConflict,
  t: (key: string, options?: Record<string, unknown>) => string,
) => {
  if (conflict.kind === 'stranded') return t('controllersStrandedHint')
  if (normalise(conflict.purpose) !== 'irrigation')
    return t('controllersTypeMismatchToNonIrrigationHint')
  if (conflict.irrigationNeeds.length === 0)
    return t('controllersTypeMismatchToIrrigationReadyHint')
  return t('controllersTypeMismatchToIrrigationHint', {
    fields: conflict.irrigationNeeds
      .map((field) => t(FIELD_LABEL_KEYS[field] ?? field))
      .join(', '),
  })
}

const COPY: Record<DecoderConflict['kind'], { title: string; body: string }> = {
  stranded: {
    title: 'controllersStrandedInfoTitle',
    body: 'controllersStrandedInfoBody',
  },
  mismatch: {
    title: 'controllersTypeMismatchInfoTitle',
    body: 'controllersTypeMismatchInfoBody',
  },
}

const DecoderConflictInfo = ({
  conflict,
  isDark,
}: {
  conflict: DecoderConflict
  isDark: boolean
}) => {
  const { t } = useTranslation()
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const copy = COPY[conflict.kind]
  const accent = isDark ? '#FDA29B' : '#B42318'

  // The grid opens a cell's editor on click and selects rows on mouse down;
  // neither should happen when the user only asked what the mark means.
  const stop = (event: MouseEvent) => event.stopPropagation()

  return (
    <>
      <IconButton
        size="small"
        aria-label={t('controllersConflictInfoLabel')}
        onMouseDown={stop}
        onClick={(event) => {
          stop(event)
          setAnchor(event.currentTarget)
        }}
        sx={{ p: '2px', flexShrink: 0, color: accent }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        onClick={stop}
        onMouseDown={stop}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              p: '12px 14px',
              maxWidth: 320,
              borderRadius: '8px',
              backgroundColor: 'background.default',
            },
          },
        }}
      >
        <Stack sx={{ gap: '6px' }}>
          <Typography
            sx={{ fontSize: '12px', fontWeight: 600, color: accent }}
          >
            {t(copy.title)}
          </Typography>
          <Typography
            sx={{ fontSize: '12px', lineHeight: 1.35, color: 'text.main' }}
          >
            {t(copy.body, {
              purpose: conflict.purpose,
              stations: conflict.stations.join(', '),
            })}
          </Typography>
          <Typography
            sx={{ fontSize: '12px', lineHeight: 1.35, color: 'grey.600' }}
          >
            {hintFor(conflict, t)}
          </Typography>
        </Stack>
      </Popover>
    </>
  )
}

export default DecoderConflictInfo
